import React, { useMemo } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useCppPartners } from '~/hooks/apis/costPerPoint';

import type { CppPartner } from '~/types/costPerPoint';

import { downloadPartnerEarnTemplate } from '../csvHelpers';
import { isEarnEligiblePartner, partnerLimitsLabel } from '../partnerEarnCredit';

export type PartnerEarnMethod = 'bulk' | 'manual';

interface Props {
  selectedMethod: PartnerEarnMethod | null;
  selectedPartner: CppPartner | null;
  onSelectMethod: (method: PartnerEarnMethod) => void;
  onSelectPartner: (partner: CppPartner | null) => void;
  onNext: () => void;
}

export default function SelectPartnerStep({
  selectedMethod,
  selectedPartner,
  onSelectMethod,
  onSelectPartner,
  onNext,
}: Props) {
  const { data, isLoading, error } = useCppPartners();
  const earnPartners = useMemo(() => (data?.result ?? []).filter(isEarnEligiblePartner), [data]);

  const handlePartnerChange = (partnerId: string) => {
    const partner = earnPartners.find((item) => item.id === partnerId) ?? null;
    onSelectPartner(partner);
  };

  const canProceed = !!selectedMethod && !!selectedPartner;

  return (
    <Box>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select earn partner
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Credits are attributed to this partner and limited by their min/max transaction rules.
        </Typography>

        {isLoading && <CircularProgress size={24} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load partners: {error.message}
          </Alert>
        )}

        {!isLoading && !error && earnPartners.length === 0 && (
          <Alert severity="warning">No active earn partners with credit permission were found.</Alert>
        )}

        {!isLoading && !error && earnPartners.length > 0 && (
          <FormControl fullWidth size="small">
            <InputLabel>Partner</InputLabel>
            <Select
              value={selectedPartner?.id ?? ''}
              label="Partner"
              onChange={(event) => handlePartnerChange(event.target.value)}
            >
              {earnPartners.map((partner) => (
                <MenuItem key={partner.id} value={partner.id}>
                  {partner.name} ({partner.partnerType}) · {partnerLimitsLabel(partner)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Choose how to credit
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Upload a CSV for many members, or look up one membership number.
        </Typography>

        <Grid container spacing={3} columns={12}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderColor: selectedMethod === 'bulk' ? 'primary.main' : 'divider',
                borderWidth: selectedMethod === 'bulk' ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onSelectMethod('bulk')} sx={{ p: 3 }}>
                <CardContent>
                  <UploadFileIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Bulk CSV upload
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credit multiple members from a CSV, validated against this partner’s limits.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                borderColor: selectedMethod === 'manual' ? 'primary.main' : 'divider',
                borderWidth: selectedMethod === 'manual' ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onSelectMethod('manual')} sx={{ p: 3 }}>
                <CardContent>
                  <PersonAddIcon color="warning" sx={{ fontSize: 40, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Manual credit
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search for a customer and credit points as this partner.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
        <Typography variant="body2" color="text.secondary">
          Bulk uploads can be reviewed before they are queued.
        </Typography>
        <Button variant="outlined" onClick={onNext} disabled={!canProceed}>
          Next Step
        </Button>
      </Stack>

      <Grid container spacing={3} mt={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Need a template?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Download the CSV template so membership numbers, points, and reasons are in the right columns.
            </Typography>
            <Button size="small" startIcon={<DownloadIcon />} onClick={downloadPartnerEarnTemplate}>
              Download CSV Template
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
